import React, { FC } from 'react';

import { Typography, Box } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import {
  userPreferredTimeFormat,
  PharmacistLegendVariant,
  BookingState,
  logos,
  convertToDecimals,
  distanceUnit,
  ServiceTypes,
  IPharmacistEvent,
  IAdminWorkshiftDetails,
} from '@pharmaplan/common';
import { ReactComponent as StarFilled } from '@pharmaplan/common/assets/icons/starFilled.svg';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import getLongTermContractLabels from '../../../common/LongTermContractLabels/LongTermContractLabels';
import { getHoursDiff } from '../../../Modals/WorkshiftDetails/utils';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomChip from '../../../common/CustomChip/CustomChip';
import WithSkeleton from '../../../common/WithSkeleton';
import useAdmin from '../../../../hooks/useAdmin';
import {
  Constants,
  ScreenTypes,
  SkeletonVariants,
} from '../../../../helpers/Constants';
import { timeString } from '../../../../helpers/Functions';
import Watchlist from '../../../common/Watchlist';
import strings from '../../../../localization';

import PharmacyInfoCard from '../../../common/PharmacyInfoCard';
import PharmacyRatingLink from '../PharmacyRatingLink';
import CustomButton from '../../../common/CustomButton';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { renderScreen } from '../../../../actions/drawerActions';
import PostedByAgencyTag from '../../../common/PostedByAgencyTag';

import { IPharmacyCard } from '../PharamcyCard';
import GoToPharmacyChat from './GoToPharmacyChat';
import classes from './style';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

const {
  variant: { text },
} = Constants;

const PharmacyCardContent: FC<IPharmacyCard> = ({
  type,
  event,
  successAction,
  CustomContent,
  showPharmacyName,
  customTitleStyle,
  goBack,
  isAutoSyncRecord,
  CustomRightItem,
  longTermShiftContract,
}) => {
  const dispatch = useAppDispatch();

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const allLogos = useAppSelector(logos);

  const isSyncedEvent = isSelfService && !isAutoSyncRecord;
  const { isAdmin } = useAdmin();

  const {
    startDate = '',
    endDate = '',
    pharmacyName,
    city,
    logoImage,
    favorite,
    bookingState,
    counterEndDate,
    counterStartDate,
    calculatedDistance,
    isInWatchlist = false,
    workShiftId,
    rating,
    pharmacyId,
  } = (event as IPharmacistEvent) ?? {};

  const {
    startDate: contractStartDate,
    endDate: contractEndDate,
    longTermShiftContractId,
  } = longTermShiftContract ?? {};

  const { logoId } = (event as IAdminWorkshiftDetails) ?? {};

  const logo = allLogos?.find((log) =>
    log.logoId === logoId)?.image;

  const isBooked = type === PharmacistLegendVariant.booked;
  const isWorkshift = type === PharmacistLegendVariant.workshift;

  const isUpdatedTiming = isBooked && bookingState === BookingState.UpdatedTiming;

  const eventChipConfig = {
    [PharmacistLegendVariant.workshift]: {
      label: strings.availableWorkshift,
    },
    [PharmacistLegendVariant.booked]: {
      label: strings.bookedWorkshift,
    },
    [PharmacistLegendVariant.requested]: {
      label: strings.requestedWorkshift,
    },
  };

  const timeConfig = {
    startDate: isUpdatedTiming ? counterStartDate : startDate,
    endDate: isUpdatedTiming ? counterEndDate : endDate,
  };

  const updatedStartDate = timeConfig?.startDate ?? '';
  const updatedEndDate = timeConfig?.endDate ?? '';

  const time = timeString(updatedStartDate, updatedEndDate, timeFormat);
  const hours = `${time} (${getHoursDiff(
    updatedStartDate,
    updatedEndDate,
  )} ${strings.hoursAbbreviation})`;

  const eventLabel = eventChipConfig[type as keyof typeof eventChipConfig].label;

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const SkeletonChip = WithSkeleton({
    successAction,
    variant: 'rounded',
  })(Box);

  const SkeletonBox = WithSkeleton({
    successAction,
    variant: 'text',
  })(Box);

  const viewRating = () => {
    dispatch(
      renderScreen({
        eventId: '',
        eventIds: [],
        pharmacistId: pharmacyId,
        workshiftId: workShiftId,
        date: startDate,
        screenNumber: 4,
        type,
        screenType: ScreenTypes.workshiftSummary,
        back: goBack,
      }),
    );
  };

  const contentHeader = () => {
    if (isBooked || isSyncedEvent || showPharmacyName) {
      return pharmacyName;
    }

    return city;
  };

  const distance = `${convertToDecimals(calculatedDistance)} ${distanceUnit}`;

  const notBookedAndNotAdmin = !isBooked && !isAdmin;
  const notWorkshiftAndNotAdmin = !isWorkshift && !isAdmin && isSyncedEvent;
  const notAdminAndIsWorkshift = !isAdmin;

  const calcRating = ((rating?.totalRating ?? 0) * 5) / 100;

  const { ShiftLabel } = getLongTermContractLabels({
    successAction,
    contractEndDate,
    contractStartDate,
    isLongTerm: !!longTermShiftContractId,
  });

  const hideHours = !longTermShiftContractId || (type === PharmacistLegendVariant.booked);

  return (
    <>
      {!!longTermShiftContractId && <ShiftLabel sx={classes.autoRecords} />}
      {isAutoSyncRecord && (
        <PostedByAgencyTag type={type} customStyle={classes.autoRecords} />
      )}
      <Box sx={[classes.card, isAutoSyncRecord ? classes.zeroPadding : {}]}>
        <Box sx={classes.cardContent}>
          <PharmacyInfoCard
            favorite={favorite}
            customTitleStyle={customTitleStyle}
            isAutoSyncRecord={isAutoSyncRecord}
            longTermAttributes={{
              contractEnd: contractEndDate,
              contractStart: contractStartDate,
              longTermShiftContractId,
            }}
            date={startDate}
            successAction={successAction}
            logoImage={logoId ? logo ?? '' : logoImage}
            title={contentHeader()}
            city={city}
            isBooked={isBooked}
            customContent={(
              <>
                {hideHours && (
                  <SkeletonTypography sx={classes.date}>
                    {hours}
                  </SkeletonTypography>
                )}

                {notAdminAndIsWorkshift && (
                  <SkeletonTypography sx={classes.location}>
                    <Box sx={classes.locationContainer}>
                      <LocationOnIcon sx={classes.icon} />
                      {distance}
                    </Box>
                  </SkeletonTypography>
                )}

                {isUpdatedTiming && (
                  <SkeletonTypography sx={classes.timeStrike}>
                    {timeString(startDate, endDate, timeFormat)}
                    (
                    {getHoursDiff(startDate, endDate)}
                    {strings.hoursAbbreviation}
                    )
                  </SkeletonTypography>
                )}

                {CustomContent
                  ?? (isSyncedEvent && (
                    <>
                      <SkeletonChip sx={classes.chipContainer}>
                        <CustomChip label={eventLabel} type={type} />
                      </SkeletonChip>

                      <SkeletonBox sx={classes.ratingContainer}>
                        <StarFilled width={11} height={11} />
                        <SkeletonTypography sx={classes.ratingText}>
                          {calcRating.toPrecision(2)}
                        </SkeletonTypography>

                        <CustomButton
                          customButtonStyle={classes.customLink}
                          label={`(${strings.viewRating})`}
                          onClick={viewRating}
                          variant={text}
                        />
                      </SkeletonBox>
                    </>
                  ))}
              </>
            )}
          />
        </Box>
        <Box sx={classes.rightContainer}>
          {notWorkshiftAndNotAdmin && (
            <GoToPharmacyChat successAction={successAction} />
          )}
          {notBookedAndNotAdmin && (
            <Box sx={classes.watchlistContainer}>
              <Watchlist
                isAutoSyncRecord={isAutoSyncRecord}
                initState={isInWatchlist}
                workshiftId={workShiftId ?? ''}
              />
            </Box>
          )}
          {CustomRightItem && (
            <Box sx={classes.rightItem}>{CustomRightItem}</Box>
          )}
        </Box>
      </Box>
      {rating && isSyncedEvent && (
        <PharmacyRatingLink successAction={successAction} rating={rating} />
      )}
    </>
  );
};

export default PharmacyCardContent;
